/**
 * SIM-SZENE LEIPZIG – Daten-Loader
 * Lädt und parst YAML-Dateien für Szenarien und Balancing.
 */
import yaml from 'js-yaml';

// Vite importiert raw files mit ?raw suffix
import scenariosYaml from './scenarios.yaml?raw';
import balancingYaml from './balancing.yaml?raw';

/**
 * Geparste Balancing-Konfiguration
 */
export const balancing = yaml.load(balancingYaml);

/**
 * Geparste Szenarien-Daten
 */
const scenariosData = yaml.load(scenariosYaml);

/**
 * Flache Liste aller Szenarien (aus allen Sections)
 * Jedes Szenario erhält zusätzlich seine Section-ID als `category`
 */
export const scenarios = scenariosData.sections.flatMap(section =>
  section.scenarios.map(scenario => ({
    ...scenario,
    category: section.id,
    categoryLabel: section.label
  }))
);

/**
 * Szenarien nach Kategorie gruppiert
 */
export const scenariosByCategory = Object.fromEntries(
  scenariosData.sections.map(section => [
    section.id,
    {
      label: section.label,
      scenarios: section.scenarios
    }
  ])
);

/**
 * Einzelnes Szenario per ID finden
 * @param {string} id - Szenario-ID
 * @returns {object|undefined} Szenario oder undefined
 */
export function getScenarioById(id) {
  return scenarios.find(s => s.id === id);
}

/**
 * Normalisiert einen Effekt-Wert basierend auf der Balancing-Skala
 * 
 * Unterstützt zwei Formate:
 * 1. Normalisiert (-3 bis +3): Wird direkt in Punkte umgewandelt
 * 2. Legacy (absolute Zahlen wie -5000): Wird relativ konvertiert
 * 
 * @param {number} value - Wert (normalisiert oder legacy)
 * @returns {number} Absoluter Effekt-Wert für 0-100 Skala
 */
export function normalizeEffect(value) {
  if (value === undefined || value === null) return 0;
  
  // Falls normalisierter Wert (-3 bis +3)
  if (Math.abs(value) <= 3) {
    return balancing.effectScale[value] ?? 0;
  }
  
  // Legacy-Wert: Konvertiere basierend auf typischen Szenarien-Werten
  // Die alten Werte reichen von ca. -80000 bis +20000
  // Wir mappen sie auf unsere -30 bis +30 Skala
  
  // Negative Werte (Verluste)
  if (value < 0) {
    if (value <= -10000) return balancing.effectScale[-3]; // Katastrophal: -30
    if (value <= -2000) return balancing.effectScale[-2];  // Schwer: -20
    if (value <= -500) return balancing.effectScale[-1];   // Leicht: -10
    return -5; // Sehr leicht
  }
  
  // Positive Werte (Gewinne)
  if (value > 0) {
    if (value >= 5000) return balancing.effectScale[3];  // Jackpot: +30
    if (value >= 1000) return balancing.effectScale[2];  // Gut: +20
    if (value >= 200) return balancing.effectScale[1];   // Leicht: +10
    return 5; // Sehr leicht
  }
  
  return 0;
}

/**
 * Berechnet den Progressions-Multiplikator basierend auf der Runde
 * Negative Effekte werden mit der Zeit stärker
 * @param {number} round - Aktuelle Runde
 * @returns {number} Multiplikator (1.0 bis maxMultiplier)
 */
export function getProgressionMultiplier(round) {
  const { enabled, startRound, maxMultiplier, maxRound } = balancing.progression;
  
  if (!enabled || round < startRound) {
    return 1.0;
  }
  
  const progress = Math.min(1, (round - startRound) / (maxRound - startRound));
  return 1 + progress * (maxMultiplier - 1);
}

/**
 * Wendet Effekte auf den State an (mit Normalisierung und Progression)
 * @param {object} effects - { geld, nerven, credibility }
 * @param {number} round - Aktuelle Runde
 * @returns {object} Normalisierte Effekte
 */
export function calculateEffects(effects, round) {
  const multiplier = getProgressionMultiplier(round);
  
  return {
    geld: applyMultiplier(normalizeEffect(effects.geld ?? 0), multiplier),
    nerven: applyMultiplier(normalizeEffect(effects.nerven ?? 0), multiplier),
    credibility: applyMultiplier(normalizeEffect(effects.credibility ?? 0), multiplier)
  };
}

/**
 * Wendet den Multiplikator nur auf negative Effekte an
 */
function applyMultiplier(value, multiplier) {
  if (value < 0) {
    return Math.round(value * multiplier);
  }
  return value;
}

/**
 * Holt die passende Auszeichnung für eine Rundenzahl
 * @param {number} rounds - Überlebte Runden
 * @returns {object|null} Auszeichnung oder null
 */
export function getAchievement(rounds) {
  const achievements = balancing.achievements
    .filter(a => rounds >= a.rounds)
    .sort((a, b) => b.rounds - a.rounds);
  
  return achievements[0] ?? null;
}

/**
 * Holt eine zufällige Game-Over-Nachricht
 * @param {string} cause - 'geld', 'nerven', oder 'credibility'
 * @returns {string} Nachricht
 */
export function getGameOverMessage(cause) {
  const messages = balancing.gameOverMessages[cause] ?? [];
  return messages[Math.floor(Math.random() * messages.length)] ?? 'Game Over.';
}
